import { IdentityImage } from "./IdentityImage";
import type { ReactNode } from "react";

/**
 * An organization's square identity tile — the same object as `RepoIdentity`,
 * one level up.
 *
 * It deliberately reuses `.sidebar-repo-identity`: the app has one neutral
 * identity-chip language (a `--surface-3` square with a hairline inset ring and
 * the initial), and an org that looked different from a repo would read as a
 * second, competing convention rather than the same idea at a larger size.
 * Uploaded icons fall back to the initial when unavailable.
 *
 * `status` is the connection badge: a 6px dot pinned to the bottom-right corner,
 * rendered only while the socket is *not* live. A permanently green dot on the
 * one control you look at most is noise; an amber one that appears when the
 * stream drops is information.
 */
export function OrgIdentity({
  name,
  image,
  size = 18,
  status,
  statusLabel,
}: {
  name: string | undefined;
  image?: string | null;
  size?: number;
  status?: ReactNode;
  statusLabel?: string;
}) {
  const initial = (name ?? "").trim().charAt(0).toUpperCase() || "O";
  return (
    <span className="org-identity" style={{ width: size, height: size }}>
      <span
        className="sidebar-repo-identity"
        /* The glyph scales with the tile so an 18px header chip and a 16px menu
           chip read as the same object at two sizes. */
        style={{ position: "relative", overflow: "hidden", width: size, height: size, fontSize: Math.round(size * 5.3) / 10 }}
      >
        {initial}
        <IdentityImage image={image} fit="contain" />
      </span>
      {/* Decoration: a badge inside a labelled button is never announced on its
          own, so the status belongs in the trigger's own accessible name and
          this stays a tooltip. */}
      {status ? <span className="org-identity-status" title={statusLabel} aria-hidden="true">{status}</span> : null}
    </span>
  );
}
